import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Tooltip } from "@mui/material";
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    AddButton,
    DataGrid,
} from "../../Common";
import CreateIcon from '@mui/icons-material/Create';

import { useHistory } from 'react-router-dom';
import Routes from '../../../routes';
import UserInfo from '../../../UserInfo';
import toast from 'react-hot-toast';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useBreadCrumbs }from '../../../hooks';
import {
    dataGridContainerProps,
    text_commonCellProps,
    select_commonCellProps,
} from '../../Common/DataGrid/utils';
import { StyledCellLink, Weight } from '../../Common/DataGrid/CellComponents';
import { getDateRangeFilterOperator, getRangeFilterOperator } from '../../Common/DataGrid/FilterComponents';
import { TranslateW } from '../../../Utils/Languages/Translate';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { Grey2 } from '../../../color_utils';
import { postRequestHeaders as headers } from "../../../utils";

const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});

const ApplianceDisposalTable = () => {
    const history = useHistory();
    
    const contractorLitePermissions = (
        UserInfo.isNativeAccountServiceProvider() && (
            UserInfo.isTenantAdministrator()
            || UserInfo.isTechnicianManager()
            || UserInfo.isTechnician()
        )
    );

    const canRecordApplianceDisposal = UserInfo.isSystemAdministrator() || contractorLitePermissions || (
        UserInfo.isNativeSystemOwner() && (
            UserInfo.isAdminRole() ||
            UserInfo.isRegionalAdministrator() ||
            UserInfo.isTechnicianManager() ||
            UserInfo.isTechnician()
        )
    );

    const canDeleteApplianceDisposal = UserInfo.isSystemAdministrator() || contractorLitePermissions || (
        UserInfo.isNativeSystemOwner() && (
            UserInfo.isSuperTenantAdmin() ||
            UserInfo.isTenantAdministrator() ||
            UserInfo.isTechnician()
        )
    );

    const canEditApplianceDisposal = UserInfo.isSystemAdministrator() || contractorLitePermissions || (
        UserInfo.isNativeSystemOwner() && (
            UserInfo.isAdminRole() ||
            UserInfo.isTechnicianManager() ||
            UserInfo.isTechnician()
        )
    );

    const navigate = useNavigate({
        pageName: 'ApplianceDisposalTable',
    });
    useBreadCrumbs({
        path: { id: 'ApplianceDisposalTable', text: 'Appliance Disposals', link: history.location.pathname, reset: true }
    });

    const [tableFilters, setTableFilters] = useState([]);
    const [refreshNeeded, setRefreshNeeded] = useState(false);

    const filters = useCallback(async () => {
        const response = await fetch(Routes.Asset.TableFilters());

        if (response.status === 200) {
            const data = await response.json();
            setTableFilters(data);
        }
    }, []);

    useEffect(() => {
        filters();
    }, [filters]);

    const deleteApplianceDisposals = async (id) => {
        try {
            const response = await fetch(Routes.Asset.DeleteApplianceDisposal(id), {
                method: "PATCH",
                headers,
            });

            if(response.status == 200) {
                toast.success('The record has been successfully deleted.', { duration: 6000 });
                setRefreshNeeded(!refreshNeeded);
            }
        } catch {
            toast.error('Error Delete Appliance Disposals', { duration: 6000 });
        }
    };

    const handleEdit = (row) => {
        navigate.push(`/EditApplianceDisposals/${row.id}`);
    };

    const handleDelete = (row) => {
        deleteApplianceDisposals(row.id);
    };

    const columns = [
        {
          field: 'name',
          headerName: 'Appliance Name',
          ...text_commonCellProps,
          renderCell: ({ row: { name, id } }) => (
            <StyledCellLink
              primaryLink
              path={`/ApplianceDisposals/${id}`}
              text={name}
              from={window.location.pathname}
            />
          )
        },
        {
            field: 'locationName',
            headerName: 'Location',
            ...select_commonCellProps,
            renderCell: ({ row: { locationName, locationId } }) => (
                <StyledCellLink
                    blueLinkStyle
                    path={`/Location/${locationId}`}
                    text={locationName}
                    from={window.location.pathname}
                />
            )
        },
        {
          field: 'materialType',
          headerName: 'Refrigerant Type',
          ...select_commonCellProps
        },
        {
            field: 'shutdownDate',
            headerName: 'Shutdown Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: `capacityLbs`,
            headerName: `Appliance Capacity (${UserInfo.getWeightUnits()})`,
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row: { capacityLbs } }) => <Weight value={capacityLbs} />
        },
        {
            field: `amountRecoveredLbs`,
            headerName: `Amount Recovered (${UserInfo.getWeightUnits()})`,
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            renderCell: ({ row: { amountRecoveredLbs } }) => <Weight value={amountRecoveredLbs} />
        },
        {
            field: `amountReclamationLbs`,
            headerName: `Amount sent to reclamation (${UserInfo.getWeightUnits()})`,
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row: { amountReclamationLbs } }) => <Weight value={amountReclamationLbs} />
        },
        {
          field: 'notes',
          headerName: 'Notes',
          ...text_commonCellProps,
          initiallyVisible: false,
          filterable: false
        },
        ...(canRecordApplianceDisposal ? [{
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row }) => {
                const actions = [];
                
                if (canEditApplianceDisposal) {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Edit Record">
                                    <CreateIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={() => handleEdit(row)}
                        />
                    );
                }
                
                if (canDeleteApplianceDisposal) {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Delete Record">
                                    <DeleteIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={() => handleDelete(row)}
                        />
                    );
                }
                
                return actions;
            }
        }] : [])
      ];

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: "Appliance Disposals" },
                ]}
            />

            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={6} md={6} lg={6}>
                    <PageTitleWithToolTip
                        pageTitle="Appliance Disposals"
                        toolTipText="Streamline documentation on the disposal of a appliance containing less 
                        than 15 lbs (or 6.80389 kgs) of refrigerant not managed in Trakref, helping ensure 
                        compliance with the minimum record-keeping requirements under 40 CFR 82.156(a)(3)."
                    />
                </Grid>
                {canRecordApplianceDisposal &&
                    <Grid item xs={6} md={6} lg={6} align="right">
                        <AddButton
                            text="Record Appliance Disposal"
                            onClick={() => navigate.push('/RecordApplianceDisposals', {from: 'Appliance Disposals'})}
                        />
                    </Grid>
                }

                <Grid item { ...dataGridContainerProps }>
                    <DataGrid
                        printLandscape
                        url={Routes.Asset.ApplianceDisposalTable()}
                        exportButtonURL={Routes.Asset.ApplianceDisposalExport()}
                        noResultText="Oops...It is empty here..."
                        exportButtonAlt={TranslateW('ExportApplianceDisposal')}
                        filterOptions={tableFilters}
                        tableId={"appliance-disposal-table"}
                        columns={columns}
                        refresh={refreshNeeded}
                    />
                </Grid>

            </Grid>
        </>
    );
};

export default ApplianceDisposalTable;
